/*
 * Checkout recheck: re-reads live availability and ship dates when the
 * shopper presses Checkout, so the order carries current dates and nothing
 * that stopped being sellable after add-to-cart reaches checkout.
 *
 * For every cart line it fetches /products/<handle>?view=cart-check (the same
 * availability chain the PDP runs, in the shopper's market) and then:
 *  - blocks checkout and names the item when the variant is now sold out or
 *    cannot ship to the shopper's country;
 *  - rewrites the Pre-Order / Custom Order line property when the date or the
 *    type changed (or drops it when the item is back in stock), then asks the
 *    shopper to review before pressing Checkout again.
 * Any network or parse failure lets checkout through unchanged (fail open):
 * Shopify checkout still enforces stock on its own.
 */
(() => {
  // cart-footer renders in both the drawer and the cart page: bind once.
  if (window.__pgCartRecheck) return;
  window.__pgCartRecheck = true;

  const DATE_PROPS = ['Pre-Order', 'Custom Order'];
  const BLOCKING_STATES = ['sold_out', 'not_shippable'];
  const TIMEOUT_MS = 4000;
  let bypass = false;
  let inFlight = false;

  const fetchJson = async (url) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const res = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(`${url} ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    let iso = value;
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (us) iso = `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const sameDate = (a, b) => formatDate(a) === formatDate(b);

  const showNotice = (form, html, isError) => {
    let notice = form.querySelector('.cart-recheck-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'cart-recheck-notice';
      notice.setAttribute('role', 'alert');
      // Outside #cart-footer: its inner HTML is replaced on every cart re-render.
      // On the cart page the footer is the right column of a flex row, so the
      // notice goes above that row.
      const footer = form.querySelector('#cart-footer');
      let anchor = footer;
      if (footer && getComputedStyle(footer.parentNode).flexDirection === 'row') anchor = footer.parentNode;
      (anchor?.parentNode || form).insertBefore(notice, anchor || null);
    }
    // Line the notice up with the checkout column (cart page and drawer differ).
    const footer = form.querySelector('#cart-footer .cart-footer') || form.querySelector('#cart-footer');
    if (footer && footer.offsetWidth) {
      notice.style.marginLeft = '0px';
      notice.style.width = '';
      const fr = footer.getBoundingClientRect();
      const nr = notice.getBoundingClientRect();
      notice.style.marginLeft = `${Math.round(fr.left - nr.left)}px`;
      notice.style.width = `${Math.round(fr.width)}px`;
    }
    notice.classList.toggle('cart-recheck-notice--error', !!isError);
    notice.innerHTML = html;
  };

  const clearNotice = (form) => form.querySelector('.cart-recheck-notice')?.remove();

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const refreshCartUi = () => {
    document.querySelectorAll('cart-items').forEach((el) => {
      if (typeof el.renderItems === 'function') el.renderItems();
    });
    document.dispatchEvent(new CustomEvent('cart:update'));
  };

  async function recheck() {
    const cart = await fetchJson('/cart.js');
    if (!cart.items?.length) return { blocked: [], updated: [] };

    const handles = [...new Set(cart.items.map((item) => item.handle))];
    const views = await Promise.all(
      handles.map((h) => fetchJson(`/products/${encodeURIComponent(h)}?view=cart-check`).catch(() => null))
    );
    const states = new Map();
    views.forEach((view) => view?.variants?.forEach((v) => states.set(v.id, v)));

    const blocked = [];
    const changes = [];
    cart.items.forEach((item) => {
      const live = states.get(item.variant_id);
      if (!live) return;
      if (BLOCKING_STATES.includes(live.state)) {
        blocked.push({ item, live });
        return;
      }
      const props = item.properties || {};
      const currentKey = DATE_PROPS.find((k) => props[k]);
      const wantKey = live.prop || '';
      // No date on the variant: the PDP writes 'Yes'; keep whatever the line has.
      const wantValue = live.date || (currentKey === wantKey ? props[currentKey] : 'Yes');
      // "Order Type: In Stock" comes from a product.json block; it is wrong on
      // any line that is not in stock now.
      const staleOrderType = live.state !== 'in_stock' && !!props['Order Type'];
      const dateChanged = currentKey !== (wantKey || undefined) || (wantKey && live.date && !sameDate(props[wantKey], live.date));
      if (!staleOrderType && !dateChanged) return;
      const next = { ...props };
      DATE_PROPS.forEach((k) => delete next[k]);
      if (staleOrderType) delete next['Order Type'];
      if (wantKey) next[wantKey] = wantValue;
      changes.push({ item, live, next, visible: dateChanged });
    });

    const updated = [];
    for (const change of changes) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      const res = await fetch('/cart/change.js', {
        signal: controller.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: change.item.key, quantity: change.item.quantity, properties: change.next }),
      });
      clearTimeout(timer);
      if (res.ok) updated.push(change);
    }
    return { blocked, updated };
  }

  // Runs the recheck and either shows a notice (returns false) or clears it
  // (returns true). Errors pass through: Shopify checkout enforces stock.
  // form can be null (Redo's banner Done button, cart closed): the cart drawer
  // opens only when there is a notice to show.
  async function gate(form) {
    let result = null;
    try {
      result = await recheck();
    } catch (error) {
      console.warn('[cart-recheck] skipped:', error);
    }

    const noticeForm = () => form || (form = openCartDrawer());
    if ((result?.blocked.length || result?.updated.some((u) => u.visible)) && !noticeForm()) {
      // No cart form on this page to show the notice in: the cart page lists it all.
      location.href = '/cart';
      return false;
    }

    if (result?.blocked.length) {
      const names = result.blocked
        .map(({ item, live }) => `<li><strong>${escapeHtml(item.product_title)}</strong>${item.variant_title ? ` (${escapeHtml(item.variant_title)})` : ''}: ${live.state === 'not_shippable' ? 'not available to ship to your country' : 'no longer available'}</li>`)
        .join('');
      showNotice(form, `<p>Please remove these items to continue to checkout:</p><ul>${names}</ul>`, true);
      if (result.updated.length) refreshCartUi();
      return false;
    }
    const visible = result?.updated.filter((u) => u.visible) || [];
    if (visible.length) {
      const lines = visible
        .map(({ item, next }) => {
          const key = DATE_PROPS.find((k) => next[k]);
          const value = key ? next[key] : '';
          const what = !key ? 'now in stock' : value === 'Yes' ? escapeHtml(key) : `${key} ships ${escapeHtml(formatDate(value))}`;
          return `<li><strong>${escapeHtml(item.product_title)}</strong>: ${what}</li>`;
        })
        .join('');
      showNotice(form, `<p>We updated the ship date on your order. Please review, then press Checkout again.</p><ul>${lines}</ul>`, false);
      refreshCartUi();
      return false;
    }
    if (form) clearNotice(form);
    return true;
  }

  const openCartDrawer = () => {
    const drawer = document.getElementById('drawer-cart');
    const form = drawer?.querySelector('form#cart');
    if (!form || !window.Woolman?.ModalsAndDrawers) return null;
    window.Woolman.ModalsAndDrawers.showModalOrDrawer('drawer-cart');
    return form;
  };

  // The visible cart form: the drawer and the cart page both render form#cart.
  const visibleCartForm = () => {
    const forms = [...document.querySelectorAll('form#cart')];
    // Only a form the shopper can see: blocking with the notice in a hidden
    // drawer would stop checkout with nothing on screen.
    // Not offsetParent: the drawer form is position: fixed, which always
    // reports null, so drawer checkouts skipped the recheck. A closed drawer is
    // visibility: hidden (slid off screen), which checkVisibility catches.
    return forms.find((f) => isShown(f) && f.querySelector('#cart-footer')) || null;
  };

  const isShown = (el) => {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ visibilityProperty: true, opacityProperty: true });
    }
    const style = getComputedStyle(el);
    return el.getClientRects().length > 0 && style.visibility !== 'hidden' && style.display !== 'none';
  };

  // Theme Checkout button: a form submit with submitter name=checkout.
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (bypass || !submitter || submitter.name !== 'checkout') return;
      event.preventDefault();
      // Other submit listeners (tracking) run once, on the real submit below.
      event.stopImmediatePropagation();
      submitter.disabled = true;
      submitter.setAttribute('aria-busy', 'true');
      const ok = await gate(form);
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
      if (!ok) return;
      bypass = true;
      try {
        form.requestSubmit(submitter);
      } finally {
        bypass = false;
      }
    },
    true
  );

  // Redo's checkout button replaces the theme button (it hides #checkout and
  // renders its own in a closed shadow root). After adding its coverage line it
  // navigates with location.href = '/checkout', with no form submit. Catch that
  // navigation, recheck, then continue to the same URL. Browsers without the
  // Navigation API go straight to checkout, as before.
  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', (event) => {
      if (bypass || !event.cancelable || event.formData || event.navigationType === 'traverse') return;
      let url;
      try {
        url = new URL(event.destination.url);
      } catch (e) {
        return;
      }
      if (url.origin !== location.origin || url.pathname !== '/checkout') return;
      const form = visibleCartForm();
      if (!form) return;
      event.preventDefault();
      // A double click on Redo's button fires two navigations: run one check.
      if (inFlight) return;
      inFlight = true;
      gate(form).finally(() => { inFlight = false; }).then((ok) => {
        if (!ok) {
          form.querySelector('.cart-recheck-notice')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        bypass = true;
        // Reset in case this navigation is cancelled (Escape, back).
        setTimeout(() => { bypass = false; }, 5000);
        location.href = url.href;
      });
    });
  }

  // Redo exchanges ("shop on site"): the shopper picks the new item here, then
  // Redo takes the #checkout click (document capture, on mousedown and click)
  // or its banner's Done button, clears the cart and sends the items to its own
  // exchange page, which makes a draft order. Neither path submits the form or
  // goes to /checkout, so the hooks above never see it. Get in first, run the
  // same check, then replay the click so Redo carries on as usual.
  const inRedoExchange = () => {
    try {
      return sessionStorage.getItem('redo.exchange-select') !== null;
    } catch (e) {
      return false;
    }
  };

  const gateThenReplay = (event, target, form) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (inFlight) return;
    inFlight = true;
    gate(form).finally(() => { inFlight = false; }).then((ok) => {
      if (!ok) {
        form?.querySelector('.cart-recheck-notice')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      bypass = true;
      try {
        target.click();
      } finally {
        bypass = false;
      }
    });
  };

  // Window capture runs before Redo's document capture listeners.
  ['mousedown', 'click'].forEach((type) => {
    window.addEventListener(
      type,
      (event) => {
        if (bypass || !inRedoExchange() || !(event.target instanceof Element)) return;
        const button = event.target.closest('#checkout');
        if (!button) return;
        // Redo completes on mousedown too: stop it there and check on the click.
        if (type === 'mousedown') {
          event.stopImmediatePropagation();
          return;
        }
        gateThenReplay(event, button, button.closest('form'));
      },
      true
    );
  });

  // The Done button lives in the banner's closed shadow root; layout/theme.liquid
  // hands us that root. A capture listener on the root runs before React's
  // onClick, which React handles on the bubble side of the same root.
  const bindRedoBanner = (root) => {
    if (root.__pgRecheckBound) return;
    root.__pgRecheckBound = true;
    root.addEventListener(
      'click',
      (event) => {
        if (bypass || !(event.target instanceof Element)) return;
        const done = event.target.closest('.redo-exchange-done-button');
        if (!done || done.disabled) return;
        gateThenReplay(event, event.target, visibleCartForm());
      },
      true
    );
  };
  window.__pgOnRedoBanner = bindRedoBanner;
  if (window.__pgRedoBannerRoot) bindRedoBanner(window.__pgRedoBannerRoot);
})();
